@let isMobile = device.isMobileUI();
@let isModalOpen = modalOpen();
@let isMenuOpen = menuOpen();
@let hasDot = showTriggerDot();
@let ariaLabel = ariaKey() | mlTranslate;
@let icon = triggerIcon();
@let texts = copy();
@let canMarkAllRead = showMarkAllRead();
@let loading = notificationsLoading();
@let empty = showEmpty();
@let unread = unreadItems();
@let earlier = earlierItems();

@if (isMobile) {
  <span [class.header-notification-wrap]="hasDot">
    <button
      mlButton
      variant="ghost"
      intent="neutral"
      size="sm"
      userClass="header-round-action"
      [attr.aria-label]="ariaLabel"
      [attr.aria-expanded]="isModalOpen"
      (click)="onOpenModal()"
    >
      <ml-icon [name]="icon" size="sm" />
    </button>
    @if (hasDot) {
      <span class="header-notification-dot" aria-hidden="true"></span>
    }
  </span>

  <ion-modal
    data-header-page-modal
    data-header-notifications-modal
    [isOpen]="isModalOpen"
    (didDismiss)="onModalDismiss()"
  >
    <ng-template>
      <ng-container [ngTemplateOutlet]="mobileSheet" />
    </ng-template>
  </ion-modal>
} @else {
  <span
    class="header-notifications-trigger-{{ kind() }}"
    [class.header-notification-wrap]="hasDot"
    [mlMenuTrigger]="notificationsMenu"
    mlMenuTriggerPlacement="bottom-end"
  >
    <button
      mlButton
      variant="ghost"
      intent="neutral"
      size="sm"
      [userClass]="isMenuOpen ? 'header-round-action header-notifications-open' : 'header-round-action'"
      [attr.aria-label]="ariaLabel"
      [attr.aria-expanded]="isMenuOpen"
      (click)="onTriggerClick()"
    >
      <ml-icon [name]="icon" size="sm" />
    </button>
    @if (hasDot) {
      <span class="header-notification-dot" aria-hidden="true"></span>
    }
  </span>

  <ml-menu #notificationsMenu panelClass="w-100 max-h-96 overflow-hidden rounded-2xl" [sheetOnMobile]="false">
    <div class="shrink-0">
      <div class="flex items-center justify-between gap-3 px-4 py-3">
        <div class="flex min-w-0 items-center gap-2">
          <h2 class="text-body font-semibold text-neutral-900">
            {{ texts.titleKey | mlTranslate }}
          </h2>
          @if (canMarkAllRead) {
            <span
              class="bg-primary-500 text-2xs inline-flex h-5 min-w-5.5 items-center justify-center rounded-full px-1.5 font-semibold text-white"
            >
              {{ unreadCount() }}
            </span>
          }
        </div>
        @if (canMarkAllRead) {
          <button
            mlButton
            variant="link"
            userClass="text-body-small text-primary h-auto px-0"
            (click)="onMarkAllRead()"
          >
            {{ 'header.notifications.markAllRead' | mlTranslate }}
          </button>
        }
      </div>
      <div class="h-px w-full bg-neutral-50" role="separator"></div>
    </div>
    <div class="min-h-0 flex-1 overflow-y-auto">
      <ng-container [ngTemplateOutlet]="notificationsBody" />
    </div>
    @if (activeKind() === 'notifications' && !loading && !empty) {
      <div class="border-splash shrink-0 border-t bg-white">
        <div class="flex items-center justify-center gap-1 px-4 py-3">
          <span class="text-body text-primary-500 font-semibold">{{ 'header.notifications.all' | mlTranslate }}</span>
          <ml-icon name="chevronRight" size="sm" class="text-primary-500" />
        </div>
      </div>
    }
  </ml-menu>
}

<ng-template #mobileSheet>
  <div class="header-page-modal" [class.platform-ios]="isIos()">
    <div class="flex items-center gap-2 px-4 py-3">
      <button
        type="button"
        class="inline-flex size-8 shrink-0 items-center justify-center text-neutral-900"
        [attr.aria-label]="'header.inbox.backAria' | mlTranslate"
        (click)="closeModal()"
      >
        <ml-icon [name]="'arrowLeft'" aria-hidden="true" />
      </button>
      <h2 class="text-body-large min-w-0 flex-1 font-semibold text-neutral-900">
        {{ 'header.inbox.title' | mlTranslate }}
      </h2>
      @if (canMarkAllRead) {
        <button type="button" class="text-body-small text-primary shrink-0" (click)="onMarkAllRead()">
          {{ 'header.notifications.markAllRead' | mlTranslate }}
        </button>
      }
    </div>

    <div class="header-notifications-toggle-bar">
      <ml-toggle-group
        class="header-notifications-toggle w-full"
        [formControl]="notificationsTab"
        [options]="toggleGroupOptions"
        [currentValue]="'notifications'"
      />
    </div>

    <div class="bg-neutral-10 flex min-h-0 flex-1 flex-col overflow-y-auto">
      <ng-container [ngTemplateOutlet]="notificationsBody" />
    </div>
  </div>
</ng-template>

<ng-template #notificationsBody>
  @if (loading) {
    @if (isMobile) {
      <div class="bg-splash min-h-0 flex-1 overflow-hidden" role="status">
        @if (selectedTab() === 'notifications') {
          @for (row of notificationSkeleton; track $index) {
            <div class="border-splash flex h-25 flex-col justify-center gap-2 border-b bg-white px-4">
              @for (width of row.lines; track $index) {
                <ml-skeleton [class]="'h-4 ' + width" />
              }
            </div>
          }
        } @else {
          @for (row of messageSkeleton; track $index) {
            <div class="border-splash flex h-30 items-center gap-3 border-b bg-white px-4">
              <ml-skeleton class="size-10 shrink-0 rounded-full" radius="full" />
              <div class="flex min-w-0 flex-1 flex-col gap-2">
                @for (width of row.lines; track $index) {
                  <ml-skeleton [class]="'h-4 ' + width" />
                }
              </div>
            </div>
          }
        }
      </div>
    } @else {
      <div role="status">
        @for (row of skeletonRows; track row) {
          <div class="flex items-center gap-2.5 px-4 py-2">
            <ml-skeleton class="size-8 shrink-0" radius="md" />
            <div class="flex min-w-0 flex-1 flex-col gap-1.5">
              <ml-skeleton class="h-3.5 w-3/4" />
              <ml-skeleton class="h-3 w-1/2" />
              <ml-skeleton class="h-3 w-1/3" />
            </div>
          </div>
        }
      </div>
    }
  } @else if (empty) {
    <div class="flex min-h-full flex-1 flex-col items-center justify-center px-6 py-8 text-center" role="status">
      <img [src]="texts.illustration" alt="" [class]="isMobile ? 'size-67.75' : 'h-26 w-29.5'" />
      <p class="text-body mt-4 font-semibold text-neutral-900">
        {{ texts.emptyTitleKey | mlTranslate }}
      </p>
      <p class="text-body-small mt-1 text-neutral-300">
        {{ texts.emptyDescriptionKey | mlTranslate }}
      </p>
    </div>
  } @else if (isMobile) {
    <div>
      @if (unread.length) {
        <div class="bg-white">
          @for (item of unread; track item.id) {
            <ng-container
              [ngTemplateOutlet]="notificationItem"
              [ngTemplateOutletContext]="{ $implicit: item, showBorder: true }"
            />
          }
        </div>
      }
      @if (earlier.length) {
        <p class="text-caption px-4 py-2 font-medium tracking-wide text-neutral-300 uppercase">
          {{ 'header.notifications.earlier' | mlTranslate }}
        </p>
        <div class="bg-white">
          @for (item of earlier; track item.id) {
            <ng-container
              [ngTemplateOutlet]="notificationItem"
              [ngTemplateOutletContext]="{ $implicit: item, showBorder: !$last }"
            />
          }
        </div>
      }
    </div>
  } @else {
    <div>
      @for (item of listedNotifications(); track item.id) {
        <ng-container
          [ngTemplateOutlet]="notificationItem"
          [ngTemplateOutletContext]="{ $implicit: item, showBorder: !$last }"
        />
      }
    </div>
  }
</ng-template>

<ng-template #notificationItem let-item let-showBorder="showBorder">
  <button type="button" class="w-full bg-white text-left" (click)="onNotificationClick(item)">
    <div class="flex items-start gap-3 px-4 py-3" [class.border-b]="showBorder" [class.border-splash]="showBorder">
      <span class="mt-1.5 size-2 shrink-0 rounded-full" [class.bg-primary-500]="!item.opened" aria-hidden="true"></span>
      <div class="min-w-0 flex-1">
        <p class="text-body truncate text-neutral-900" [class.font-semibold]="!item.opened">
          {{ item.subject }}
        </p>
        <p class="text-body-small mt-0.5 line-clamp-2 text-neutral-300">{{ item.body }}</p>
        <p class="text-caption mt-0.5 truncate text-neutral-300">
          {{ item.created | mlTimeAgo }}
          @if (item.facility) {
            · {{ item.facility }}
          }
        </p>
      </div>
    </div>
  </button>
</ng-template>
